import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Users, Star, TrendingUp } from 'lucide-react';

const SocialProof = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });

  const [count, setCount] = useState(0);

  useEffect(() => {
    if (inView) {
      let start = 0;
      const end = 3200;
      const duration = 2000;
      const increment = end / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 16);

      return () => clearInterval(timer);
    }
  }, [inView]);

  return (
    <section ref={ref} className="py-20 md:py-32 bg-gradient-to-br from-[#0D7FA3] via-[#1a5f7a] to-[#C64DAD] relative overflow-hidden">
      {/* Animated background */}
      <motion.div
        animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full blur-3xl opacity-10" />


      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center">

            {/* Animated counter */}
            <motion.div
              initial={{ scale: 0 }}
              animate={inView ? { scale: 1 } : {}}
              transition={{ duration: 0.8, type: "spring" }}
              className="inline-flex items-center justify-center w-32 h-32 bg-white/20 backdrop-blur-md rounded-full mb-8 border-4 border-white/30">

              <div className="text-center">
                <motion.div
                  className="font-black text-white text-3xl"
                  key={count}>

                  {count}+
                </motion.div>
              </div>
            </motion.div>

            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Mais de <span className="text-yellow-300">{count}+ alunos</span>
              <br />
              já estudam inglês com o EW Pass
            </h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-xl md:text-2xl text-white/90 mb-12 leading-relaxed max-w-3xl mx-auto">

              Pessoas comuns, com rotina cheia, que decidiram{' '}
              <span className="font-bold text-yellow-300">parar de recomeçar do zero</span>{' '}
              e passar a evoluir com estrutura.
            </motion.p>

            {/* Stats */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
              { icon: Users, value: '3200+', label: 'Alunos ativos' },
              { icon: Star, value: '4.9', label: 'Avaliação média' },
              { icon: TrendingUp, value: '95%', label: 'Satisfação' }].
              map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="bg-white/15 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-300">

                    <Icon className="w-10 h-10 text-yellow-300 mx-auto mb-4" strokeWidth={2} />
                    <div className="text-4xl font-black text-white mb-2">
                      {stat.value}
                    </div>
                    <div className="text-white/80 font-medium">
                      {stat.label}
                    </div>
                  </motion.div>);

              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};

export default SocialProof;